// Grid System for Scene Editor
import { System } from '@/engine/systems/System';
import { logger } from '@/engine/logging';
import { sceneState } from '../stores/sceneEditorStore';
import * as PIXI from 'pixi.js';

export class GridSystem extends System {
  private bgContainer?: PIXI.Container;
  private grid?: PIXI.Graphics;
  private bgWidth = 1080; // Default reference width
  private bgHeight = 1920; // Default reference height
  private unsubscribeState: (() => void) | null = null;

  constructor(scene: any) {
    super(scene);
  }

  /**
   * Initialize grid system with background container
   */
  initialize(bgContainer: PIXI.Container, width: number, height: number) {
    this.bgContainer = bgContainer;
    this.bgWidth = width;
    this.bgHeight = height;

    this.createGrid();
    this.subscribeToState();

    logger.info('🔲 Grid System initialized', {
      source: 'grid-system',
      bgContainer: !!this.bgContainer,
      grid: !!this.grid,
      width,
      height,
    });
  }

  protected onStart(): void {
    // System already initialized
  }

  protected onStop(): void {
    this.cleanup();
  }

  protected onUpdate(_deltaTime: number): void {
    // Grid doesn't need updates
  }

  protected onDestroy(): void {
    this.cleanup();
  }

  /**
   * Update background size (called on resize)
   */
  resize(width: number, height: number) {
    this.bgWidth = width;
    this.bgHeight = height;
    this.updateGrid();

    logger.info('🔲 Grid resized', {
      source: 'grid-system',
      width,
      height,
    });
  }

  private cleanup() {
    this.unsubscribeState?.();
    this.unsubscribeState = null;
    this.grid?.destroy();
    this.grid = undefined;
  }

  private createGrid() {
    if (!this.bgContainer) {
      logger.warn('⚠️ Cannot create grid - bgContainer not available', { source: 'grid-system' });
      return;
    }

    this.grid?.destroy();
    this.grid = new PIXI.Graphics();

    // Grid in bgContainer above background, below content
    this.grid.zIndex = 50;
    this.bgContainer.addChild(this.grid);
    this.updateGrid();

    logger.info('🔲 Grid created', {
      source: 'grid-system',
      grid: !!this.grid,
      bgContainer: !!this.bgContainer,
      gridVisible: this.grid.visible,
      zIndex: this.grid.zIndex,
    });
  }

  private subscribeToState() {
    if (this.unsubscribeState) return;

    this.unsubscribeState = sceneState.subscribe(() => {
      // Update grid when state changes (grid settings or viewport)
      this.updateGrid();
    });
  }

  private updateGrid() {
    if (!this.grid) return;

    const state = sceneState.get();

    if (!state.grid.enabled) {
      this.grid.visible = false;
      return;
    }

    this.grid.clear();
    this.grid.visible = true;
    this.grid.alpha = state.grid.opacity;

    // Grid in bgContainer, static position
    this.grid.position.set(0, 0);

    const gridSize = state.grid.size;

    // Don't draw grid if cells are too small or too large
    if (gridSize < 5 || gridSize > 5000) {
      logger.info('🔲 Grid size out of range, skipping draw', {
        source: 'grid-system',
        gridSize,
        minSize: 5,
        maxSize: 5000,
      });
      return;
    }

    // Draw grid with thin gray lines
    const lineColor = 0x666666;
    const lineAlpha = 0.3;
    const lineWidth = 1;

    // Vertical lines
    for (let x = 0; x <= this.bgWidth; x += gridSize) {
      this.grid.moveTo(x, 0);
      this.grid.lineTo(x, this.bgHeight);
      this.grid.stroke({ width: lineWidth, color: lineColor, alpha: lineAlpha });
    }

    // Horizontal lines
    for (let y = 0; y <= this.bgHeight; y += gridSize) {
      this.grid.moveTo(0, y);
      this.grid.lineTo(this.bgWidth, y);
      this.grid.stroke({ width: lineWidth, color: lineColor, alpha: lineAlpha });
    }

    logger.info('🔲 Grid updated', {
      source: 'grid-system',
      gridVisible: this.grid.visible,
      gridAlpha: this.grid.alpha,
      gridSize,
      bgWidth: this.bgWidth,
      bgHeight: this.bgHeight,
    });
  }
}
